import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Card, Icon, Statistic, Table, Tag, Tooltip, message } from 'antd';

import { upsertDailyBalance, upsertDeclaration } from '../../utils/registerRecon/registerDB';
import { SITE_TYPE, SITE_TYPE_LABEL, TOLERANCE_CL } from '../../utils/registerRecon/reconEngine';
import { fromCl } from '../../utils/registerRecon/registerNormalize';
import { fmtL } from '../../utils/tableKit';

/*
 * The whole reconciliation, on one screen.
 *
 * The reviewer types the closing balance of each tank (or, at a site with no
 * tank, the fuel issued for the period). If it matches AssetPro the entries
 * behind it are taken to be right and there is nothing else to do.
 *
 * Where corrections have been recorded against entries, the ADJUSTED AssetPro
 * figure is shown next to the unadjusted one — never instead of it. Nothing here
 * is written back to AssetPro, so the original figure stays visible until it is
 * actually fixed there.
 */

const money = (v) => (v === null || v === undefined ? '—' : fmtL(v));
const L = (cl) => (cl === null || cl === undefined ? null : fromCl(cl));

function BalancesPanel({ recon, periodKey, scopeKey, scopeNote, periodTo, onChanged, onGoToDaily, onGoToEntries }) {
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  // Seed from the store; re-seed when the period, scope or target set changes.
  // Derived during render rather than in an effect, so the inputs are never
  // briefly blank and a saved value cannot be clobbered by a stale draft.
  const seedKey = `${periodKey}|${scopeKey}|${recon.targets.map((t) => t.key).join(',')}`;
  const seededRef = useRef(null);
  if (seededRef.current !== seedKey) {
    seededRef.current = seedKey;
    const next = {};
    recon.targets.forEach((t) => { next[t.key] = L(t.regCl); });
    setDraft(next);
    setDirty(false);
  }

  const patch = useCallback((key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setDirty(true);
  }, []);

  const save = (quiet = false) => {
    const tanks = {};
    let issuedLtr = null;
    let directLtr = null;
    recon.targets.forEach((t) => {
      const v = draft[t.key];
      if (t.kind === 'TANK_CLOSING') tanks[t.storageId] = v === undefined ? null : v;
      if (t.kind === 'ISSUED') issuedLtr = v === undefined ? null : v;
      if (t.kind === 'DIRECT') directLtr = v === undefined ? null : v;
    });
    try {
      upsertDeclaration({ periodKey, scopeKey, tanks, issuedLtr, directLtr });
      // The period closing IS the closing on the last day, so entering it here
      // fills that day in on the Day-wise tab rather than making someone type
      // the same number twice.
      recon.targets.forEach((t) => {
        // Not direct fuel: its period figure is the SUM of the days, not the last
        // one, so writing it onto the closing day would be a different number.
        // Direct days are entered on the Day-wise tab and add up to this instead.
        if (t.kind === 'DIRECT') return;
        const v = draft[t.key];
        upsertDailyBalance({
          periodKey,
          scopeKey,
          target: t.kind === 'TANK_CLOSING' ? t.storageId : 'ISSUED',
          date: periodTo,
          valueLtr: v === undefined ? null : v,
        });
      });
      setDirty(false);
      setSavedAt(Date.now());
      if (!quiet) message.success('Saved.');
      onChanged();
    } catch (e) {
      message.error(e.message);
    }
  };

  // Enter saves and moves to the next figure, so a multi-tank site can be typed
  // straight through without reaching for the Save button between each one.
  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    save(true);
    const boxes = [...document.querySelectorAll('[data-recon-target]')];
    const i = boxes.indexOf(e.target);
    const next = i >= 0 ? boxes[i + 1] : null;
    if (next) { next.focus(); next.select(); }
    else e.target.blur();
  };

  const columns = [
    {
      title: 'What is being reconciled',
      dataIndex: 'label',
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {v}
            {r.optional && (
              <Tooltip title="The bowser balance above already settles the period at a single-tank site. Enter this to check the direct fuel as well; leaving it blank will not hold the reconciliation open.">
                <Tag style={{ marginLeft: 8 }}>optional</Tag>
              </Tooltip>
            )}
          </div>
          {r.subLabel && <div style={{ fontSize: 12, color: '#999' }}>{r.subLabel}</div>}
          <div style={{ fontSize: 12, color: '#bbb' }}>{r.hint}</div>
        </div>
      ),
    },
    {
      title: <span>Per the register <Tag color="orange">enter</Tag></span>,
      key: 'reg',
      width: 170,
      render: (v, r) => (
        <input
          type="number"
          className="ant-input"
          data-recon-target={r.key}
          style={{ width: 150, textAlign: 'right', fontFamily: 'monospace' }}
          placeholder="litres"
          value={draft[r.key] === null || draft[r.key] === undefined ? '' : draft[r.key]}
          onChange={(e) => patch(r.key, e.target.value === '' ? null : Number(e.target.value))}
          onKeyDown={onKeyDown}
        />
      ),
    },
    {
      title: 'Per AssetPro',
      dataIndex: 'apCl',
      width: 150,
      align: 'right',
      render: (v, r) => (
        <div>
          <div style={{ fontFamily: 'monospace' }}>{money(L(v))}</div>
          {r.hasCorrections && (
            <Tooltip title="AssetPro's own figure, adjusted by the corrections you have recorded against its entries. Nothing has been written back to AssetPro.">
              <div style={{ fontFamily: 'monospace', color: '#1890ff', fontSize: 12 }}>
                {money(L(r.adjustedApCl))} <Tag color="blue" style={{ marginLeft: 2 }}>adjusted</Tag>
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Difference',
      key: 'diff',
      width: 170,
      align: 'right',
      render: (v, r) => {
        if (r.adjustedDiffCl === null) {
          return <span style={{ color: '#faad14' }}>enter the figure</span>;
        }
        if (r.reconciled) {
          return (
            <span>
              <Tag color="green" style={{ fontFamily: 'monospace', fontSize: 14 }}><Icon type="check" /> matches</Tag>
              {r.hasCorrections && r.matchedBefore === false && (
                <div style={{ fontSize: 11, color: '#52c41a' }}>after your corrections</div>
              )}
            </span>
          );
        }
        return (
          <Tag color="red" style={{ fontFamily: 'monospace', fontSize: 14 }}>{money(L(r.adjustedDiffCl))}</Tag>
        );
      },
    },
    {
      title: '',
      key: 'act',
      width: 140,
      // Every figure that is out gets the same way in, direct fuel included: go
      // to its own day-wise and find the day it happened.
      render: (v, r) => (r.reconciled === false
        ? <Button size="small" onClick={() => onGoToDaily(r.key)}>Find the day</Button>
        : null),
    },
  ];

  const headline = () => {
    if (recon.state === 'NOT_STARTED') {
      return (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={recon.siteType === SITE_TYPE.DIRECT_ONLY
            ? 'Enter the fuel issued for this period, as per the register'
            : 'Enter the closing balance of each tank, as per the register'}
          description="If it matches AssetPro, the entries behind it are taken to be correct and the reconciliation is done."
        />
      );
    }
    if (recon.state === 'INCOMPLETE') {
      return (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${recon.entered} of ${recon.total} figures entered`}
          description="Nothing is assumed to be zero, so the rest cannot be checked until they are filled in."
        />
      );
    }
    if (recon.state === 'RECONCILED') {
      return (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 12 }}
          message={recon.reconciledByCorrections ? 'Reconciled — your corrections closed the difference' : 'Reconciled'}
          description={recon.reconciledByCorrections
            ? 'The figures did not match as they stood. With the corrections you recorded against AssetPro entries applied, they now do. Those corrections still need actioning in AssetPro — the worklist is on the Entries tab.'
            : 'AssetPro agrees with the register. The entries behind these figures are taken to be correct.'}
        />
      );
    }
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 12 }}
        message={`Does not match — ${recon.outstanding.length} figure(s) out`}
        description={
          <div>
            <div style={{ marginBottom: 8 }}>
              Go day by day, newest first. Each day is measured from the last one you enter, so a day
              showing a difference is a day where AssetPro and the register actually moved apart —
              not one carrying a gap down from earlier. Check that day's entries and mark them off or
              correct them; a correction made days ago pulls the closing balance back into line, and
              that is counted here.
            </div>
            <Button
              type="primary"
              size="small"
              onClick={() => onGoToDaily(recon.outstanding.length ? recon.outstanding[0].key : null)}
            >
              Find the day it broke
            </Button>
            <Button size="small" style={{ marginLeft: 8 }} onClick={() => onGoToEntries(null)}>Go straight to the entries</Button>
          </div>
        }
      />
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 10 }}>
        <Tag color="blue">{SITE_TYPE_LABEL[recon.siteType]}</Tag>
        {scopeNote && <Tag>{scopeNote}</Tag>}
        <span style={{ fontSize: 12, color: '#999' }}>
          {recon.siteType === SITE_TYPE.DIRECT_ONLY
            && 'No tank in this scope, so the check is simply that the fuel issued matches.'}
          {recon.siteType === SITE_TYPE.SINGLE_STORAGE
            && 'One tank. The register writes the direct purchase and its issue together, so they cancel and the bowser balance settles the period on its own — direct fuel is shown below for the filtered scope so it can be checked too, but it is optional.'}
          {recon.siteType === SITE_TYPE.MULTI_STORAGE
            && 'Several tanks, so each closing balance is checked, plus the fuel bought and issued direct to machines.'}
        </span>
      </div>

      {headline()}

      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
          <Statistic
            title="Figures entered"
            value={recon.entered}
            suffix={`/ ${recon.total}`}
            valueStyle={{ fontSize: 20 }}
          />
          {recon.requiredTotal < recon.total && (
            <span style={{ fontSize: 12, color: '#999', alignSelf: 'flex-end', marginBottom: 4 }}>
              {recon.requiredTotal} required, the rest optional
            </span>
          )}
          <Statistic
            title="Still out"
            value={recon.outstanding.length}
            valueStyle={{ fontSize: 20, color: recon.outstanding.length ? '#f5222d' : '#52c41a' }}
          />
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: '#999', marginRight: 12 }}>
            {dirty
              ? <span style={{ color: '#fa8c16' }}>Unsaved changes — press Enter or Save</span>
              : savedAt ? 'Saved' : 'Press Enter in a box to save'}
          </span>
          <Button type="primary" icon="save" disabled={!dirty} onClick={() => save(false)}>Save</Button>
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={recon.targets}
        rowKey="key"
        size="middle"
        pagination={false}
        locale={{ emptyText: 'Nothing in scope. Fetch a period, or widen the Location filter.' }}
      />
    </div>
  );
}

export default BalancesPanel;
